import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { HiararchicalExternalDocument, ExternalDocument } from '../../models/estensa-models.model';
import { CultureService } from '../../services/culture.service';
import { DateTimePipe } from '@se/se.ui.html5';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { seSetSelectExternalDoc, seUpdateSelectExternalDoc } from '../redux/external-docs.actions';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { RootState } from '../../root-store/root.state';
import { Subscription } from 'rxjs';
import { DocFamilyType as ExternalDocFamilyType } from 'estensa-module';

@Component({
    selector: 'se-external-docs-tree',
    templateUrl: './se-external-docs-tree.component.html',
    styleUrls: ['./se-external-docs-tree.component.scss']
})
export class SeExternalDocsTreeComponent implements OnInit, OnDestroy {

    @ViewChild('fileInput') fileInput: ElementRef;

    idReferto = "F12BDCC7-A147-4246-BFFC-83D3730BB991";
    idConsenso = "A2948E0C-5F3E-4FA4-BD57-2BB30133CCB1";
    idAssicurazione = "84C94665-479E-46EE-9224-8D25F7F24E7D";
    idAltro = "B70A8DC4-90D8-4F5E-987A-647FA61B7785";

    @Output()
    deleteRequired = new EventEmitter<ExternalDocument>();

    @Output()
    editRequired = new EventEmitter<ExternalDocument>();

    @Output()
    addRequired = new EventEmitter<ExternalDocument>();

    @Input()
    externalDocuments: HiararchicalExternalDocument[];

    @Input()
    isLoadingDoc: boolean = false;

    private _selectedDoc: ExternalDocument;
    private _autoscrollEnabled = true;

    get disableButton(): boolean {
        return !!this.isLoadingDoc;
    }

    _setSelectedDocSubscription: Subscription;
    _updateSelectedDocSubscription: Subscription;

    constructor(private culture: CultureService,
        private translate: TranslateService,
        private store: Store<RootState>,
        private actions: Actions,
    ) {
    }

    public onSelect(document: ExternalDocument) {
        if (!this.isLoadingDoc) { //blocco la selezione se stiamo caricando
            this._autoscrollEnabled = false;
            this.store.dispatch(seSetSelectExternalDoc({
                doc: document
            }));
        }
    }

    public isDocSelected(document: ExternalDocument) {
        return (this._selectedDoc) && (document.id == this._selectedDoc.id);
    }

    public onDeleteDocumentRequired(document: ExternalDocument) {
        this.deleteRequired.emit(document);
    }

    public onEditDocumentRequired(document: ExternalDocument) {
        this.editRequired.emit(document);
    }

    public onHandleFileInput(files: FileList, documents: HiararchicalExternalDocument) {
        const fileToUpload = files.item(0);
        if (!fileToUpload) {
            // Operazione cancellata.
            return;
        }

        // Set File Information
        const file = {
            fileName: fileToUpload.name,
            fileSize: fileToUpload.size,
            fileType: fileToUpload.type,
            lastModifiedTime: fileToUpload.lastModified,
            fileAsBase64: null
        }

        // Use FileReader() object to get file to upload
        // NOTE: FileReader only works with newer browsers
        let reader = new FileReader();

        // Setup onload event for reader
        reader.onload = () => {

                let docBase64ByteArray: string;
                let fileType: number;

                // Dato che usiamo readAsDataURL(), reader.result è una stringa.
                file.fileAsBase64 = reader.result.toString();

                // Filtro solo i tipi possibili
                if (this.startsWith(file.fileAsBase64, "data:application/pdf;base64,")) {
                    docBase64ByteArray = file.fileAsBase64.split("data:application/pdf;base64,")[1];
                    fileType = 1; // PDF
                }
                else if (this.startsWith(file.fileAsBase64, "data:image/jpeg;base64,")) {
                    docBase64ByteArray = file.fileAsBase64.split("data:image/jpeg;base64,")[1];
                    fileType = 2; // JPEG
                }
                else if (this.startsWith(file.fileAsBase64, "data:image/png;base64,")) {
                    docBase64ByteArray = file.fileAsBase64.split("data:image/png;base64,")[1];
                    fileType = 3; // PNG
                }

                const externalDoc: ExternalDocument = {
                    id: Date.now().toString(), // Tanto serve solo qui, poi sul server non lo consifdera
                    accessionNumber: '',
                    addToPatientDossier: false,
                    creationDateTime: null,
                    docByteArray: null,
                    docBase64ByteArray: docBase64ByteArray,
                    docType: this.idReferto, // consenso informaato, referto.....
                    eprDescription: '',
                    eventDateTime: new Date(Date.now()),
                    fileName: file.fileName,
                    fileType: fileType, // 1 - PDF, 2 - JPEG, 3 - PNG
                    guidExaminationList: (documents.type == ExternalDocFamilyType.Patient as any) ? null : [documents.id], // 0 - Patient, 2 : StudyComplete,
                    guidPatient: '',
                    instanceNumber: '1',
                    issuerOfPatientID: '',
                    modality: 'DOC',
                    mode: (documents.type == ExternalDocFamilyType.Patient as any) ? 0 : 2, // 0 - Patient, 2 : StudyComplete
                    patientBirthDate: null,
                    patientID: '',
                    patientName: '',
                    patientSex: '',
                    patientSurname: '',
                    referringPhysicianName: '',
                    sopInstanceUID: '',
                    seriesInstanceUID: '',
                    seriesNumber: '',
                    studyDateTime: null,
                    studyID: '',
                    studyInstanceUID: '',
                    userID: '',
                }

                // Serve se sceglie di nuovo lo stesso file, altrimenti non scatta la change....
                this.fileInput.nativeElement.value = null;

                this.addRequired.emit(externalDoc);
        }

        reader.readAsDataURL(fileToUpload);// Read the PDF file
    }

    public getCardTitle(doc): string {
        let fileType = "";
        let title = "";
        switch (doc.fileType) {
            case 1:
                fileType = "PDF";
                break;
            case 2:
                fileType = "JPEG";
                break;

            default:
                fileType = "PNG";
        }

        switch (doc.docType.toUpperCase()) {
            case "F12BDCC7-A147-4246-BFFC-83D3730BB991":
                title = this.translate.instant('Finding') + " (" + fileType + ")";
                break;
            case "A2948E0C-5F3E-4FA4-BD57-2BB30133CCB1":
                title = this.translate.instant('ExtDoc_Privacy') + " (" + fileType + ")";
                break;
            case "84C94665-479E-46EE-9224-8D25F7F24E7D":
                title = this.translate.instant('ExtDoc_Assurance') + " (" + fileType + ")";
                break;
            default:
                title = this.translate.instant('ExtDoc_Other') + " (" + fileType + ")";
        }

        return title;
    }

    public ngOnInit(): void {
        this.listenForStoreEvents();
    }

    public ngOnDestroy(): void {
        this._setSelectedDocSubscription.unsubscribe();
        this._updateSelectedDocSubscription.unsubscribe();
    }

    public getDateForPatient(document) {
        let s = '';
        if (document.creationDateTime)
            s += this.translate.instant('CreationDate') + ': ' +  this.getDate(document.creationDateTime);

        if (document.eventDateTime) {
            if (s)
                s += ' -  ';

            s += this.translate.instant('EventDate') + ': ' + this.getDate(document.eventDateTime);
        }

        return s;
    }

    public getDateForStudy(document) {
        let s = '';
        if (document.creationDateTime)
            s += this.translate.instant('CreationDate') + ': ' + this.getDate(document.creationDateTime);

        return s;
    }

    public getDossierStatus(document): string {
        return this.translate.instant("PatientDossier") + ": " +
            ((document.addToPatientDossier) ? this.translate.instant("Yes") : this.translate.instant("No"));
    }

    public scrollToSelected = () => {
        setTimeout(() => {
            const element = document.querySelector('.se-external-docs-selected-mat-card');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: "center", inline: "nearest" });
            }
        }, 200)
    }

    private listenForStoreEvents() {

        this._setSelectedDocSubscription = this.actions.pipe(ofType(
            seSetSelectExternalDoc)).subscribe(
                (value) => {
                    // E' cambiata la selezione
                    if (value.doc == null
                        || !this._selectedDoc
                        || (this._selectedDoc && value.doc.id != this._selectedDoc.id)) {
                        this._selectedDoc = value.doc;
                        if (this._autoscrollEnabled)
                            this.scrollToSelected();

                        this._autoscrollEnabled = true;
                    }
                });

        this._updateSelectedDocSubscription = this.actions.pipe(ofType(
            seUpdateSelectExternalDoc)).subscribe(
                (value) => {
                    this._selectedDoc = value.doc;
                });
    }

    private getDate(value): string {
        try {
            let m = moment(value);
            if (m.isValid) {
                let dateTimePipe = new DateTimePipe(this.culture.getCulture());
                return dateTimePipe.transform(value, this.culture.getShortDatePattern());
            }
            else
                return "N/A";
        }
        catch{
            return "N/A";
        }
    }

    private startsWith(sourceString: string, startingString: string) {
        // IE non supporta la funzione.... (es6)
        if (!sourceString.startsWith) {
            let position = 0;
            return sourceString.indexOf(startingString, 0) === position;
        }

        return sourceString.startsWith(startingString);
    }
}
